import {
  AutocompleteInput,
  Datagrid,
  Filter,
  FunctionField,
  PaginationLimit,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SearchInput,
  TextField,
} from "react-admin";
import StatusField from "../../../components/StatusField";
import CustomPriceField from "../../../components/CustomPriceField";
import EmptyComponent from "../../subcontractorPurchaseInvoices/components/EmptyComponent";
import React from "react";
import { makeStyles } from "@material-ui/core";
import BulkConfirmationButton from "./BulkConfirmationButton";
import ApproveButton from "./ApproveButton";
import RejectButton from "./RejectButton";
import { availableOnlySuperAdmin } from "utils/validation";
import { PaddingDenseStyled2List } from "../../../components/ListWrapper";
import ActionsMenu from "../../../components/ActionsMenu";
import CustomPagination from "../../../components/CustomPagination";
import UpdatedByField from "../../../components/UpdatedByField";

interface ISubcontractorRouteTransactionsTable {
  isFixedHeight?: boolean;
  purchaseInvoiceSummaryId: number;
  subcontractorId: number;
  status?: "Pending" | "Approved" | "Rejected";
}

const useStyles = makeStyles({
  RaLists: {
    "& div:nth-child(2) > div": {
      marginTop: "0px",
    },
  },
});

const SubcontractorRouteTransactionsTableFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />

    {props.isNotFilteredBySubcontractor && (
      <ReferenceInput
        reference="subcontractors"
        source="SubcontractorId"
        filterToQuery={(searchText) => {
          if (searchText !== "") return { search: searchText };
        }}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
    )}

    <ReferenceInput
      source="SchoolId"
      reference="schools"
      filterToQuery={(searchText) => {
        if (searchText !== "") return { search: searchText };
      }}
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const SubcontractorRouteTransactionsTable = ({
  isFixedHeight,
  purchaseInvoiceSummaryId,
  subcontractorId,
  status,
}: ISubcontractorRouteTransactionsTable) => {
  const classes = useStyles();
  const isPending = status === "Pending";
  const isNotFilteredBySubcontractor = !subcontractorId;

  return (
    <ResourceContextProvider value="subcontractorRouteTransactions">
      <PaddingDenseStyled2List
        basePath="/subcontractorRouteTransactions"
        className={classes.RaLists}
        actions={null}
        exporter={false}
        hasCreate={false}
        filters={
          <SubcontractorRouteTransactionsTableFilter
            isNotFilteredBySubcontractor={isNotFilteredBySubcontractor}
          />
        }
        filterDefaultValues={{
          SubcontractorPurchaseInvoiceSummaryId: purchaseInvoiceSummaryId,
          SubcontractorId: subcontractorId,
          Status: status,
        }}
        sort={{ field: "id", order: "DESC" }}
        bulkActionButtons={
          isPending && availableOnlySuperAdmin() && <BulkConfirmationButton />
        }
        title=" "
        pagination={
          <CustomPagination
            limit={isFixedHeight ? <EmptyComponent /> : <PaginationLimit />}
          />
        }
      >
        <Datagrid>
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor Identifier"
            reference="subcontractors"
            link={false}
          >
            <TextField source="subcontractorIdentifier" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor MBC Code"
            reference="subcontractors"
            link={false}
          >
            <TextField source="mbcVendorId" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            reference="subcontractors"
            link={false}
          >
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <ReferenceField source="schoolId" reference="schools" link={false}>
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <TextField source="routeName" emptyText="-" />
          <CustomPriceField source="baseAmount" />
          <CustomPriceField source="adjustmentAmount" />
          <CustomPriceField source="busAssistantIncentive" />
          <CustomPriceField source="additionalIncentive" />
          <CustomPriceField source="schoolIncentive" />
          <CustomPriceField source="erpCharges" />
          <StatusField source="status" />
          {!isPending && (
            <FunctionField
              label="Updated By"
              render={(record: any) => (
                <>
                  {record.updatedBy === null ? (
                    "-"
                  ) : (
                    <UpdatedByField data={record.updatedBy} />
                  )}
                </>
              )}
            />
          )}

          {isPending && availableOnlySuperAdmin() && (
            <FunctionField
              label="Actions"
              render={(record: any) => (
                <ActionsMenu
                  render={[
                    <ApproveButton id={record.id} />,
                    <RejectButton id={record.id} />,
                  ]}
                />
              )}
            />
          )}
        </Datagrid>
      </PaddingDenseStyled2List>
    </ResourceContextProvider>
  );
};

export default SubcontractorRouteTransactionsTable;
